import React from 'react';
import { useHistory } from 'react-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { AccountInfoAtom, LoginStatusAtom } from '../../../store/AccountStore';
import { LoginOverlayVisible } from '../../../store/OverlayStore';
import scrollIntoView from './scrollIntoView';
import './HtmlContent.scss';
import { SubscriptionModalAtom } from '../../../InkyPen/SubscriptionModal/SubscriptionModalAtom';

const srcScriptRegex = /<script\b[^>]*src=['"]([^'"]+)['"][^>]*><\/script>/gi;
const asyncScriptRegex = /<script\b[^>]*async\b[^>]*><\/script>/gi;
const inlineScriptRegex = /<script\b[^>]*>([\s\S]*?)<\/script>/gi;

interface HtmlContentProps {
    content: string;
    loadScripts?: boolean;
}

export default function HtmlContent({ content, loadScripts = false }: HtmlContentProps) {
    const containerRef = React.useRef<HTMLDivElement>(null);
    useScripts(loadScripts, content);
    const onClick = useSpecialClickHandlers(containerRef);
    const contentWithoutScripts = React.useMemo(
        () => content.replace(srcScriptRegex, '').replace(inlineScriptRegex, ''),
        [],
    );
    return (
        <div
            ref={containerRef}
            onClick={onClick}
            className='html'
            dangerouslySetInnerHTML={{ __html: contentWithoutScripts }}
        />
    );
}

function useScripts(loadScripts: boolean, content: string) {
    React.useEffect(() => {
        if (!loadScripts) {
            return;
        }

        // do not load scripts if the user agent is RenderBot
        if (window.navigator.userAgent.includes('RenderBot')) {
            return;
        }

        const inlineScripts: Record<number, HTMLElement> = {};
        window.requestAnimationFrame(async () => {
            let match: RegExpExecArray;

            while ((match = srcScriptRegex.exec(content)) !== null) {
                const src = match[1];
                const key = hash(src);
                const id = `script-${key}`;
                if (document.getElementById(id)) {
                    continue;
                }
                const el = document.createElement('script');
                el.setAttribute('id', id);
                el.setAttribute('data-cfasync', 'false');
                const isAsync = asyncScriptRegex.test(content);
                if (isAsync) {
                    el.async = true;
                }
                el.src = src;
                document.body.appendChild(el);
                if (!isAsync) {
                    await Promise.race([
                        new Promise<void>(resolve => {
                            el.addEventListener('load', () => {
                                resolve();
                            }, { once: true });
                        }),
                        new Promise<void>(resolve => {
                            window.setTimeout(() => {
                                resolve();
                            }, 5000);
                        }),
                    ]);
                }
            }

            while ((match = inlineScriptRegex.exec(content)) !== null) {
                const code = match[1].trim();
                const key = hash(code);
                const id = `script-${key}`;
                if (document.getElementById(id)) {
                    continue;
                }
                const el = document.createElement('script');
                el.setAttribute('id', id);
                el.setAttribute('data-cfasync', 'false');
                el.type = 'text/javascript';
                el.text = '(() => {\n' + code + '\n})();\n';
                document.body.appendChild(el);
                inlineScripts[key] = el;
            }
        });

        return () => {
            for (const key in inlineScripts) {
                inlineScripts[key].remove();
            }
        };
    }, [loadScripts, content]);
}

function hash(str: string): number {
    let hash = 0;
    if (str.length === 0) {
        return hash;
    }
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash |= 0; // convert to 32bit integer
    }
    return hash;
}

function useSpecialClickHandlers(containerRef: React.RefObject<HTMLElement | null>) {
    const handleLoginPopupClicks = useHandleLoginPopupClicks();
    const handleSubscriptionPopupClicks = useHandleSubscriptionPopupClicks();
    const handleAnchorClicks = useHandleAnchorClicks();
    return React.useCallback((event: React.MouseEvent) => {
        if (handleLoginPopupClicks(containerRef.current, event)) {
            return;
        }
        if (handleSubscriptionPopupClicks(containerRef.current, event)) {
            return;
        }
        if (handleAnchorClicks(containerRef.current, event)) {
            return;
        }
    }, [containerRef, handleLoginPopupClicks, handleSubscriptionPopupClicks, handleAnchorClicks]);
}

function useHandleLoginPopupClicks() {
    const loginStatus = useAtomValue(LoginStatusAtom);
    const setVisible = useSetAtom(LoginOverlayVisible);
    return React.useCallback((root: HTMLElement | null, event: React.MouseEvent) => {
        const target = event.target as HTMLElement;
        if (!isLoginPopupLink(root, target)) {
            return false;
        }
        event.preventDefault();
        if (loginStatus !== 'LoggedIn') {
            setVisible(true);
        }
        return true;
    }, [loginStatus, setVisible]);
}

function useHandleSubscriptionPopupClicks() {
    const accountInfo = useAtomValue(AccountInfoAtom);
    const setVisible = useSetAtom(SubscriptionModalAtom);
    return React.useCallback((root: HTMLElement | null, event: React.MouseEvent) => {
        const target = event.target as HTMLElement;
        if (!isSubscriptionPopupLink(root, target)) {
            return false;
        }
        event.preventDefault();
        if (!accountInfo.subscription.isActive) {
            setVisible(true);
        }
        return true;
    }, [accountInfo, setVisible]);

}

function isLoginPopupLink(root: HTMLElement | null, target: HTMLElement): boolean {
    let el: HTMLElement | null = target;
    while (el && el !== root) {
        if (el.tagName === 'A' && el.getAttribute('href')?.endsWith('#login-popup')) {
            return true;
        }
        if (el.classList.contains('action-login-popup')) {
            return true;
        }
        if (root === null) {
            return false;
        }
        el = el.parentElement;
    }
    return false;
}

function isSubscriptionPopupLink(root: HTMLElement | null, target: HTMLElement): boolean {
    let el: HTMLElement | null = target;
    while (el && el !== root) {
        if (el.classList.contains('action-subscription-popup')) {
            return true;
        }
        if (root === null) {
            return false;
        }
        el = el.parentElement;
    }
    return false;
}

function useHandleAnchorClicks() {
    const { replace } = useHistory();
    return React.useCallback((root: HTMLElement | null, event: React.MouseEvent): boolean => {
        const target = event.target as HTMLElement;
        if (!isAnchorLink(root, target)) {
            return false;
        }
        const href = (target.getAttribute('href') ?? '');
        const id = href.substring(1);
        const scrollTarget = document.getElementById(id);
        if (scrollTarget) {
            event.preventDefault();
            scrollIntoView(scrollTarget, { smooth: true });
            replace(href);
        }
        return true;
    }, [replace]);
}

function isAnchorLink(root: HTMLElement | null, target: HTMLElement | null): boolean {
    let el: HTMLElement | null = target;
    while (el && el !== root) {
        if (el.tagName === 'A' && el.getAttribute('href')?.startsWith('#')) {
            return true;
        }
        if (root === null) {
            return false;
        }
        el = el.parentElement;
    }
    return false;
}
